import { Accessor, createSignal, Setter, Show } from "solid-js";
import { useTelescopeContext } from "@hubble/telescope";
import { VoucherProductMetadata } from "~/server/types/brand";
import { goBack } from "~/shared_states/modal";

type UserInfoModalProps = {
  onClickSubmit: (profileUpdateParams: any) => void;
  email: string | null;
  phone: string | null;
  name: string | null;
  subtitle: string;
  brandDetails?: VoucherProductMetadata;
  clientLogoUrl?: string;
  setShowLoader: Setter<boolean>;
  showLoader: Accessor<boolean>;
  errors: Accessor<{ [key: string]: string }>;
  setErrors: Setter<{ [key: string]: string }>;
  collectPhone?: boolean;
  collectName?: boolean;
};

export default function RnrUserDataCollectionModal(props: UserInfoModalProps) {
  const { components } = useTelescopeContext();
  const [name, setName] = createSignal<string>(props.name ?? "");
  const [email, setEmail] = createSignal<string>(props.email ?? "");
  const [phone, setPhone] = createSignal<string>(props.phone ?? "");
  const [visitedFields, setVisitedFields] = createSignal<{
    [key: string]: boolean;
  }>({});

  const { errors, setErrors } = props;

  function isNameAlreadyPresent() {
    return !!props.name;
  }

  function isEmailAlreadyPresent() {
    return !!props.email;
  }

  function isPhoneAlreadyPresent() {
    return !!props.phone;
  }

  const shouldCollectPhone = () => props.collectPhone !== false;
  const shouldCollectName = () => props.collectName !== false;

  const updateUserInfo = async () => {
    let body: any = {};
    if (shouldCollectName() && !isNameAlreadyPresent()) {
      body = {
        ...body,
        firstName: name().split(" ")[0],
        lastName: name().split(" ").slice(1).join(" "),
      };
    }
    if (!isEmailAlreadyPresent()) {
      body = {
        ...body,
        email: email(),
      };
    }
    if (shouldCollectPhone() && !isPhoneAlreadyPresent()) {
      body = {
        ...body,
        phoneNumber: phone(),
      };
    }

    props.onClickSubmit(body);
  };

  const validateName = (name: string) => {
    const nameRegex = /^[A-Za-z\s]{2,50}$/;
    return nameRegex.test(name.trim());
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^[1-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validateAndUpdateErrors = () => {
    const newErrors: { [key: string]: string } = {};
    const visited = visitedFields();

    if (shouldCollectName() && visited["name"]) {
      if (!name()) {
        newErrors["name"] = "Please enter your full name";
      } else if (!validateName(name())) {
        newErrors["name"] = "Please enter your full name";
      }
    }

    if (visited["email"]) {
      if (!email()) {
        newErrors["email"] = "Please enter your email address";
      } else if (!validateEmail(email())) {
        newErrors["email"] = "Please enter a valid email address";
      }
    }

    if (shouldCollectPhone() && visited["phone"]) {
      if (!phone()) {
        newErrors["phone"] = "Please enter your Whatsapp phone no.";
      } else if (!validatePhone(phone())) {
        newErrors["phone"] = "Please a valid phone no.";
      }
    }

    setErrors(newErrors);
    return newErrors;
  };

  return (
    <form
      onSubmit={onSubmit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onSubmit(e);
        }
      }}
      class="telescope-autofill-fix flex flex-col bg-background-normal-primary"
    >
      <components.ModalHeader
        size="medium"
        title="Just one more step"
        description={props.subtitle}
        onClose={() => goBack()}
      />

      <div class="flex flex-col gap-4 px-5 py-5">
        <Show when={shouldCollectName()}>
          <components.Input
            label="Your name"
            value={name()}
            onChange={(value) => {
              setName(value);
              setVisitedFields((prev) => ({ ...prev, name: true }));
              if (errors()["name"]) {
                validateAndUpdateErrors();
              }
            }}
            placeholder="Enter full name"
            size={40}
            state={
              isNameAlreadyPresent()
                ? "disabled"
                : errors()["name"]
                  ? "error"
                  : "default"
            }
            helperText={errors()["name"]}
          />
        </Show>

        <components.Input
          label="Your email"
          value={email()}
          onChange={(value) => {
            setEmail(value);
            setVisitedFields((prev) => ({ ...prev, email: true }));
            if (errors()["email"]) {
              validateAndUpdateErrors();
            }
          }}
          placeholder="Enter your email id"
          size={40}
          state={
            isEmailAlreadyPresent()
              ? "disabled"
              : errors()["email"]
                ? "error"
                : "default"
          }
          helperText={errors()["email"]}
        />

        <Show when={shouldCollectPhone()}>
          <components.Input
            label="Phone no."
            value={phone()}
            onChange={(value) => {
              setPhone(value);
              setVisitedFields((prev) => ({ ...prev, phone: true }));
              if (errors()["phone"]) {
                validateAndUpdateErrors();
              }
            }}
            placeholder="10 digit phone no."
            size={40}
            type="phone"
            state={
              isPhoneAlreadyPresent()
                ? "disabled"
                : errors()["phone"]
                  ? "error"
                  : "default"
            }
            helperText={errors()["phone"]}
          />
        </Show>

        <div class="text-label-regular text-text-normal-secondary">
          Your privacy is our priority. We never share your data with anyone
        </div>
      </div>

      <div class="mt-auto">
        <components.ModalFooter
          button={{
            text: "Continue",
            onClick: onSubmit,
            variant: "primary",
            style: "neutral",
            size: 40,
            loading: props.showLoader(),
            disabled: props.showLoader() || Object.keys(errors()).length > 0,
          }}
        />
      </div>
    </form>
  );

  function onSubmit(e: Event) {
    e.preventDefault();
    if (props.showLoader() || Object.keys(errors()).length > 0) return;
    // Mark all fields as visited when clicking submit
    setVisitedFields({
      name: shouldCollectName(),
      email: true,
      phone: shouldCollectPhone(),
    });
    const newErrors = validateAndUpdateErrors();
    if (Object.keys(newErrors).length > 0) return;
    updateUserInfo();
  }
}
